import {useMemo} from "react";
import {Select} from "@mantine/core";
import type {LabelGroup} from "../types";
import styles from "./checklist-config.module.css";

type GlobalFilterSettingsProps = {
    viewGroups: LabelGroup[];
    selectedLabels: Record<string, string>;
    onPatchDimensions: (dimensions: string[]) => void;
    /** Container class — lets each viewport own its layout without forking the
     *  shared selectable-group logic below. Defaults to the desktop sidebar grid. */
    className?: string;
};

/**
 * The global "filter" settings — the collection's `viewGroups` selects that
 * tailor the checklist content (e.g. "Ordinær / Ekstraordinær GF", company form).
 *
 * Shared by the desktop sidebar (ChecklistConfig) and the narrow-viewport toolbar
 * Collapse. The single-sourced business logic is the `labels.length > 1`
 * selectable-group rule and the `onChange → onPatchDimensions([val])` wiring, so
 * the two views can never disagree on *which* groups are filterable. Only the
 * container layout (via `className`) differs per viewport.
 */
export const GlobalFilterSettings = ({
    viewGroups,
    selectedLabels,
    onPatchDimensions,
    className,
}: GlobalFilterSettingsProps) => {
    const selectableGroups = useMemo(
        () => viewGroups.filter((g) => g.labels.length > 1),
        [viewGroups]
    );

    if (selectableGroups.length === 0) return null;

    return (
        <div className={className ?? styles.comboboxContainer}>
            {selectableGroups.map((group) => (
                <Select
                    key={group.id}
                    data={group.labels.map((l) => ({
                        value: l.id,
                        label: l.name,
                    }))}
                    value={selectedLabels[group.id]}
                    onChange={(val) => val && onPatchDimensions([val])}
                    size="sm"
                    variant="filled"
                    className={styles.combobox}
                    comboboxProps={{
                        width: "auto",
                        position: "bottom-start",
                        classNames: {dropdown: styles.comboboxDropdown},
                    }}
                />
            ))}
        </div>
    );
};
